import { UiGridFiltersService } from '@uigy/shared/components/ui-grid/services/ui-grid-filters.service';
import { DataChipIcons, DataChipInput, LinkParams } from '@uigy/shared/components/data-chip/interface';
import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import {
  Asset,
  AssetType,
  PolicyFindingFilterField,
  ExprOp,
  FindingStatus,
  LeanAsset,
  PolicyEventType,
} from '@uigy/generated/graphql';
import { IconsEnum } from '@uigy/generated/icons.enum';
import { RoutesStatesNamesEnum } from '@uigy/shared/enums/routesStatesNames.enum';
import {
  PillsInput,
  SourceDestination,
} from '@uigy/shared/components/ui-drawer/components/ui-drawer-content-header/interface';
import {
  PillTypes,
} from '@uigy/shared/components/ui-drawer/components/ui-drawer-content-header/ui-drawer-content-header.enums';
import {
  StatusPillType,
} from '@uigy/shared/components/ui-status-pill/ui-status-pill-typed/ui-status-pill-typed.enums';
import { DateTimeService } from '@uigy/shared/services/date-time/date-time.service';
import { DatesFormatsEnum } from '@uigy/shared/enums/dateFormats.enum';
import { LanguageService } from '@uigy/shared/services/language.service';
import { ActivatedRoute } from '@angular/router';
import {
  PolicyFindingsService,
} from '@uigy/modules/findings/components/findings-policy-violations/services/policy-findings.service';
import { FindingsTabQueryParams, TabGroup } from '@uigy/shared/components/tabs/tabs.enums';
import {
  DrawerSubSection,
  DrawerSubSectionProps,
} from '@uigy/shared/components/ui-drawer/components/ui-drawer-section/interface';
import { DrawerTab, Tab, TabConfig, TabsSize, TabsVariant } from '@uigy/shared/components/tabs/tabs.interface';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActionButtonDefinition, ActionButtonVariant } from '@uigy/shared/components/ui-buttons-action-bar/models';
import { NsEnum } from '@uigy/shared/constants/language.constants';
import { EventType, PolicyEventTypeEnums } from '@uigy/shared/constants/eventTypes.constant';
import { PoliciesDataHelperService } from '@uigy/modules/policies/services/policiesDataHelper.service';
import { EventsTypesService } from '@uigy/shared/services/eventsTypes.service';
import { SubSectionType } from '@uigy/shared/components/ui-drawer/components/ui-drawer-section/constants';
import {
  RiskScoreCellRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/risk-score-cell-renderer/risk-score-cell-renderer.component';
import {
  CriticalityCellRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/criticality-cell-renderer/criticality-cell-renderer.component';
import {
  AssetTypeCellRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/asset-type-cell-renderer/asset-type-cell-renderer.component';
import {
  VendorRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/vendor-renderer/vendor-renderer.component';
import {
  LongTextCellRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/long-text-cell-renderer/long-text-cell-renderer.component';
import {
  PolicyFindingsGridSettingsService,
} from '@uigy/modules/findings/components/findings-policy-violations/services/policy-findings-grid-settings.service';
import {
  RouterRendererComponent,
} from '@uigy/shared/modules/grid/cell-renderers/router-renderer/router-renderer.component';
import { ColDef, ValueGetterParams } from '@ag-grid-community/core';
import {
  SeverityCellRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/severity-cell-renderer/severity-cell-renderer.component';
import { PolicyFindingDetailedFragment } from '@uigy/generated/graphql-worker';
import { DelimitersEnum } from '@uigy/shared/enums/delimiters.enum';
import { purdueLevel } from '@uigy/shared/constants/purdue-level.constant';
import {
  RemovedEntityNameRendererComponent,
} from '@uigy/shared/modules/grid/cell-renderers/removed-entity-name-renderer/removed-entity-name-renderer.component';
import { AppIdsEnum } from '@uigy/shared/directives/app-id/appIds.enum';
import { PolicyBackendNameEnum } from '@uigy/modules/policies/enums/policy.enum';
import { EventTypeParam, PolicyIdParam } from '@uigy/modules/policies/constants/policy.constant';
import { IndegyICellRendererParams, MenuItem } from '@uigy/shared/models/interfaces';
import { PolicyGridItem } from '@uigy/modules/policies/models/policies';
import { GroupTypesEnum } from '@uigy/modules/groups/enums/groupTypes.enum';
import { PoliciesGridSettingsService } from '@uigy/modules/policies/constants/policies-grid-settings.service';
import {
  ComplexGroupsRouterRendererComponent,
} from '@uigy/shared/modules/grid/cell-renderers/complex-groups-router-renderer/complex-groups-router-renderer.component';
import { GridUtils } from '@uigy/shared/components/ui-grid/grid-utils';
import {
  PolicyFindingsActionsService,
} from '@uigy/modules/findings/components/findings-policy-violations/services/policy-findings-actions.service';
import {
  RegisteredDrawerHandle,
  SideDrawerUrlParametersService,
} from '@uigy/shared/services/url-params/side-drawer-url-parameters.service';
import { FindingsDrawerQueryParams } from '@uigy/shared/components/ui-drawer/drawer.enums';
import Utils from '@uigy/utils';

@Injectable({
  providedIn: 'root',
})
export class PolicyFindingsSideDrawerService {
  //region services
  private languageService = inject(LanguageService);
  private findingsService = inject(PolicyFindingsService);
  private route = inject(ActivatedRoute);
  private eventsTypesService = inject(EventsTypesService);
  private policyFindingsGridSettingsService = inject(PolicyFindingsGridSettingsService);
  private policiesGridSettingsService = inject(PoliciesGridSettingsService);
  private actionsService = inject(PolicyFindingsActionsService);
  private policiesDataHelperService = inject(PoliciesDataHelperService);
  private sideDrawerUrlParametersService = inject(SideDrawerUrlParametersService);
  private drawerHandle?: RegisteredDrawerHandle;


  //endregion

  //region signals
  public selectedFinding = signal<PolicyFindingDetailedFragment | undefined>(undefined);
  private similarFindings = signal({
    [FindingStatus.Active]: 0,
    [FindingStatus.Resolved]: 0
  });

  public isLoading = signal<boolean>(true);
  public isDrawerOpen = signal<boolean>(false);
  public selectedTabIndex = signal<number>(0); // Track internal tab index instead of using queryParams
  public isEmpty = signal<boolean>(false);

  public tabs: Signal<Tab[]> = computed(() => {
    const drawerTabsResult = this.drawerTabs();
    const resetTabs: Tab[] = drawerTabsResult.map(drawerTab => {
      return drawerTab.tab;
    });
    if (resetTabs.length > 0) {
      resetTabs[0].selected = true;
    }

    return resetTabs;
  });

  public drawerTabs = signal<DrawerTab[]>([]);

  // Computed signals for reactive tab content
  private detailsSubSections = computed(() => this._getDetailsSubSections());
  private sourceSubSections = computed(() => this._getSourceSubSections());
  private destinationSubSections = computed(() => this._getDestinationSubSections());
  private policySections = computed(() => this._getPolicySections());

  public menuItems: Signal<MenuItem[]> = computed(() => {
    const finding = this.selectedFinding();
    if (!finding) {
      return [];
    }
    return this.actionsService.drawerMenuItemsForFinding(finding);
  });

  //endregion

  //region constants
  public readonly findingsTexts = this.languageService.texts.findings.policy_violations.side_drawer;
  public readonly tabConfig: TabConfig = {
    tabId: TabGroup.ActiveQueriesTab,
    size: TabsSize.small,
    variant: TabsVariant.rounded
  };
  private readonly keyValueGridComponents = {
    riskScoreRenderer: RiskScoreCellRendererComponent,
    criticalityRenderer: CriticalityCellRendererComponent,
    assetTypeRenderer: AssetTypeCellRendererComponent,
    assetVendorRenderer: VendorRendererComponent,
    longTextCellRenderer: LongTextCellRendererComponent,
    routerCellRenderer: RouterRendererComponent,
    severityCellRendererComponent: SeverityCellRendererComponent,
    removedEntityComponent: RemovedEntityNameRendererComponent,
    complexGroupsRouterRendererComponent: ComplexGroupsRouterRendererComponent
  };

  //endregion

  public constructor() {
    this.drawerHandle = this.sideDrawerUrlParametersService.registerDrawer({
      paramKey: FindingsDrawerQueryParams.SelectedPolicyFindingId,
      getCurrentId: () => this.selectedFinding()?.id,
      isOpen: () => this.isDrawerOpen(),
      onSelect: (id) => {
        this.isDrawerOpen.set(true);
        if (id !== this.selectedFinding()?.id) {
          this.loadFinding(id);
        }
      },
      onClear: () => {
        this.selectedFinding.set(undefined);
        this.isDrawerOpen.set(false);
        this.updateDrawerTabs();
      },
      openHistoryMode: 'push',
      closeHistoryMode: 'push'
    });
    this.updateDrawerTabs();
  }

  private loadFinding(findingId: string): void {
    this.isLoading.set(true);
    this.isEmpty.set(false);
    this.selectedTabIndex.set(0);
    this.findingsService.getFindingById(findingId).subscribe((finding) => {
      if (!finding) {
        this.isEmpty.set(true);
      }
      this.selectedFinding.set(finding);
      const policyId = this.selectedFinding()?.policy?.id;
      if (policyId) {
        this.findingsService.getSimilarFindings(policyId).subscribe(similarFindings => {
          this.similarFindings.set(similarFindings);
          this.updateDrawerTabs();
          this.isLoading.set(false);
        });
      } else {
        this.updateDrawerTabs();
        this.isLoading.set(false);
      }
    });
  }

  public setSelectedFinding(findingId: string | undefined, options?: { skipUrlUpdate?: boolean }): void {
    if (findingId) {
      if (options?.skipUrlUpdate) {
        this.isDrawerOpen.set(true);
        if (findingId !== this.selectedFinding()?.id) {
          this.loadFinding(findingId);
        }
      } else {
        this.drawerHandle?.open(findingId, { historyMode: 'push' });
      }
    } else {
      this.closeDrawer(options);
    }
  }

  public closeDrawer(options?: { skipUrlUpdate?: boolean }): void {
    if (options?.skipUrlUpdate) {
      this.selectedFinding.set(undefined);
      this.isDrawerOpen.set(false);
      this.updateDrawerTabs();
    } else {
      this.drawerHandle?.close({ historyMode: 'push' });
    }
  }

  //region Drawer Header
  public headerTopSectionDataChip(): DataChipInput {
    return {
      label: this.findingsTexts.header.policy_violation,
      leftIcons: [{ generalIcon: IconsEnum.bell }],
    };
  }

  public headerPills(): PillsInput[] {
    const pills: PillsInput[] = [];
    const severity = this.selectedFinding()?.severity;
    if (severity) {
      pills.push(
        { pillType: PillTypes.policy, pillInput: severity },
      );
    }
    const status = this.selectedFinding()?.status;
    if (status) {
      pills.push(
        { pillType: PillTypes.status, pillInput: { pillType: StatusPillType.findings, pillStatus: status } },
      );
    }
    return pills;
  }

  public headerSourceDestination(): SourceDestination {
    return {
      source: this._src_dest_formatting((this.selectedFinding()?.srcAssets?.nodes as LeanAsset[]) ?? [], (this.selectedFinding()?.srcIps as string[]) ?? []),
      destination: this._src_dest_formatting((this.selectedFinding()?.dstAssets?.nodes as LeanAsset[]) ?? [], (this.selectedFinding()?.dstIps as string[]) ?? []),
    };
  };

  private _src_dest_formatting(assets: LeanAsset[], ips: string[]): DataChipInput {
    let dataChip: DataChipInput;
    let leftIcons: DataChipIcons[];

    if (assets?.length) {
      leftIcons = [{ assetType: assets[0].type || AssetType.Unknown }];
      if (assets[0].vendor) {
        leftIcons.push({ vendorName: assets[0].vendor });
      }

      dataChip = {
        links: assets.map(asset => {
          return {
            displayName: asset.name || '',
            path: this._assetExternalURL(asset.id),
            isExternal: true,
            target: '_blank'
          };
        }),
        leftIcons: leftIcons,
      };
    } else {
      const values = ips?.filter(ip => ip != null && ip != '') ?? [] ;
      dataChip = {
        values: values.length ? values : [this.findingsTexts.header.unknown_src_dst]
      };
    };

    return dataChip;
  }

  public headerBottomSectionDataChipsBottom(): DataChipInput[] {
    const trend = this.selectedFinding()?.trend;
    let trendIcon : DataChipIcons | undefined;
    if (trend && trend?.hasTrend) {
      const trendValue: number = (trend.lastTrendWindow || 0) - (trend.prevTrendWindow || 0);

      if (trendValue > 0) {
        trendIcon = { generalIcon: IconsEnum.trending_up,
          color: '--icons-disruptive',
          tooltip: this.languageService.getTranslationTyped('findings.policy_violations.side_drawer.header.trend.tooltip.trending_up', {last_trend_window_value: trend.lastTrendWindow, prev_trend_window_value: trend.prevTrendWindow})
        };
      } else if (trendValue < 0) {
        trendIcon = { generalIcon: IconsEnum.trending_down,
          color: '--icons-good',
          tooltip: this.languageService.getTranslationTyped('findings.policy_violations.side_drawer.header.trend.tooltip.trending_down', {last_trend_window_value: trend.lastTrendWindow, prev_trend_window_value: trend.prevTrendWindow})
        };
      } else {
        trendIcon = { generalIcon: IconsEnum.trending_unchanged,
          color: '--icons-tertiary',
          tooltip: this.languageService.getTranslationTyped('findings.policy_violations.side_drawer.header.trend.tooltip.trending_unchanged', {last_trend_window_value: trend.lastTrendWindow})
        };
      }
    }

    const dataChips: DataChipInput[] = [
      {
        label: this.findingsTexts.header.trend.label,
        values: [(this.selectedFinding()?.activeHits || 0).toString()],
        rightIcon: trendIcon
      }
    ];

    if (this.selectedFinding()?.status === FindingStatus.Resolved) {
      dataChips.push(
        {
          label: this.findingsTexts.header.resolved_at,
          values: [DateTimeService.utcToLocalFormatted(this.selectedFinding()?.resolvedOn, DatesFormatsEnum.ShortMonthDayYear) || ''],
        },
      );
    } else {
      dataChips.push(
        {
          values: [DateTimeService.utcToLocalFormatted(this.selectedFinding()?.firstHitTime, DatesFormatsEnum.ShortMonthDayYear) + ' - ' + DateTimeService.utcToLocalFormatted(this.selectedFinding()?.lastHitTime, DatesFormatsEnum.ShortMonthDayYear)],
        },
      );
    }

    const filterActive = GridUtils.convertUiGridFilterToAgGridFilter({
      op: ExprOp.And,
      expressions: [
        { field: PolicyFindingFilterField.Status, op: ExprOp.In, values: [FindingStatus.Active, FindingStatus.Resurfaced] },
        { field: PolicyFindingFilterField.PolicyTitle, op: ExprOp.In, values: [this.selectedFinding()?.policy.title] },
      ],
    });

    const filterFixed = GridUtils.convertUiGridFilterToAgGridFilter({
      op: ExprOp.And,
      expressions: [
        { field: PolicyFindingFilterField.Status, op: ExprOp.In, values: [FindingStatus.Resolved] },
        { field: PolicyFindingFilterField.PolicyTitle, op: ExprOp.In, values: [this.selectedFinding()?.policy.title] },
      ],
    });

    const links: LinkParams[]  = [
      {
        displayName: `${this.similarFindings()[FindingStatus.Active]} ${this.findingsTexts.header.similar_findings.active}`,
        path: `${RoutesStatesNamesEnum.risks}/${RoutesStatesNamesEnum.findings}?${TabGroup.FindingsTab}=${FindingsTabQueryParams.PolicyViolations}&filters=${UiGridFiltersService.encodeURIFilters(JSON.stringify(filterActive.filters))}`,
        isExternal: true,
      },
      {
        displayName: `${this.similarFindings()[FindingStatus.Resolved]} ${this.findingsTexts.header.similar_findings.resolved}`,
        path: `${RoutesStatesNamesEnum.risks}/${RoutesStatesNamesEnum.findings}?${TabGroup.FindingsTab}=${FindingsTabQueryParams.PolicyViolations}&filters=${UiGridFiltersService.encodeURIFilters(JSON.stringify(filterFixed.filters))}`,
        isExternal: true,
      }
    ];

    dataChips.push(
      {
        label: this.findingsTexts.header.similar_findings.label,
        links: links,
        separateMultipleValues: true
      },
    );

    return dataChips;
  }

  //endregion

  //region Tabs
  private updateDrawerTabs(): void {
    const tabs: DrawerTab[] = [
      {
        tab: {
          title: this.findingsTexts.tabs.details.title,
          selected: true,
          content: this.detailsSubSections(),
          appId: AppIdsEnum.findings_pv_details
        },
        section: {
          sectionTitle: this.findingsTexts.tabs.details.section.section_title,
          sectionSubTitle: this.languageService.getTranslationTyped('findings.policy_violations.side_drawer.tabs.details.section.section_subtitle', {
            active_hits: this.selectedFinding()?.activeHits || 0,
            total_hits: (this.selectedFinding()?.resolvedHits || 0) + (this.selectedFinding()?.activeHits || 0)
          }),
          actionButton: [
            {
              variant: ActionButtonVariant.copy,
              label: this.findingsTexts.tabs.details.section.actions.copy,
              textToCopy: this.selectedFinding()?.id ?? '',
              showLabel: true
            },
            {
              variant: ActionButtonVariant.link,
              title: this.findingsTexts.tabs.details.section.actions.events_log,
              path: `${RoutesStatesNamesEnum.events}/${RoutesStatesNamesEnum.allEvents}/${RoutesStatesNamesEnum.eventsDetailsTab}`,
              isExternal: true
            }
          ],
        }
      },
    ];

    const sources = this.sourceSubSections();
    if(sources.length > 0) {
      tabs.push({
        tab: {
          title: this.findingsTexts.tabs.source.title,
          counter: sources.length > 1 ? sources.length : undefined,
          content: this.sourceSubSections(),
          appId: AppIdsEnum.findings_pv_src
        },
        section: {
          sectionTitle: sources.length > 1 ? this.findingsTexts.tabs.source.section.title_multiple : this.findingsTexts.tabs.source.section.title_single,
          actionButton: sources.length === 1 ? [this._assetLinkActionButton(this.selectedFinding()?.srcAssets?.nodes[0].id || '', this.findingsTexts.tabs.source.subsections.link)] : []
        }
      });
    }

    const dest = this.destinationSubSections();
    if(dest.length > 0) {
      tabs.push({
        tab: {
          title: this.findingsTexts.tabs.destination.title,
          counter: dest.length > 1 ? dest.length : undefined,
          content: this.destinationSubSections(),
          appId: AppIdsEnum.findings_pv_dest
        },
        section: {
          sectionTitle: dest.length > 1 ? this.findingsTexts.tabs.destination.section.title_multiple : this.findingsTexts.tabs.destination.section.title_single,
          actionButton: dest.length === 1 ? [this._assetLinkActionButton(this.selectedFinding()?.dstAssets?.nodes[0].id || '', this.findingsTexts.tabs.destination.subsections.link)] : []
        }
      });
    }

    tabs.push({
      tab: {
        title: this.findingsTexts.tabs.policy.title,
        content: this.policySections(),
        appId: AppIdsEnum.findings_pv_policy
      },
      section: {
        sectionTitle: this.findingsTexts.tabs.policy.section.title,
        actionButton: [
          {
            variant: ActionButtonVariant.link,
            title: this.findingsTexts.tabs.policy.section.link,
            path: `${RoutesStatesNamesEnum.settingsDataCollection}/${RoutesStatesNamesEnum.policies}/${RoutesStatesNamesEnum.singlePolicyPage}?${PolicyIdParam}=${this.selectedFinding()?.policy?.id}&${EventTypeParam}=${this.selectedFinding()?.eventType?.type}`,
            isExternal: true
          }
        ],
      }
    });

    this.drawerTabs.set(tabs);
  }

  public getCurrentTabTitle(): string {
    const tabIndex = this.selectedTabIndex() || 0;
    const currentTabs = this.drawerTabs();
    return currentTabs[tabIndex].section.sectionTitle || '';
  }

  public getCurrentTabSubTitle(): string | undefined {
    const tabIndex = this.selectedTabIndex() || 0;
    const currentTabs = this.drawerTabs();
    return currentTabs[tabIndex].section.sectionSubTitle;
  }

  public getCurrentActionButton(): ActionButtonDefinition[] {
    const tabIndex = this.selectedTabIndex() || 0;
    return this.drawerTabs()[tabIndex]?.section.actionButton || [];
  }

  public onTabChanged(event: MatTabChangeEvent): void {
    this.selectedTabIndex.set(event.index);
  }

  public getDrawerSectionsForCurrentTab(): DrawerSubSection[] {
    const tabIndex = this.selectedTabIndex();
    return this.drawerTabs()[tabIndex].tab.content as DrawerSubSection[];
  }

  //endregion

  //region Details Tab subsections
  private _getDetailsSubSections(): DrawerSubSection[] {

    const subsections: DrawerSubSection[] = [
      {
        title: this.findingsTexts.tabs.details.subsections.violation_details.title,
        subSectionProps: {
          ...this._violationDetailsSubSections(),
        }
      },
    ];

    const whyText = this._whyImportantMsg();
    if (whyText) {
      subsections.push(
        {
          title: this.findingsTexts.tabs.details.subsections.why_important.title,
          subSectionProps: {
            subSectionType: SubSectionType.longText,
            subSectionContent: {
              text: whyText,
            },
          }
        }
      );
    }

    const mitigation = this._mitigationMsg();
    if (mitigation) {
      subsections.push(
        {
          title: this.findingsTexts.tabs.details.subsections.suggest_mitigation.title,
          subSectionProps: {
            subSectionType: SubSectionType.longText,
            subSectionContent: {
              text: mitigation,
            },
          }
        }
      );
    }

    if (this.selectedFinding()?.pluginName) {
      subsections.push({
        title: this.findingsTexts.tabs.details.subsections.plugin.title,
        subSectionProps: {
          ...this._pluginDetailsSubSections(),
        }
      });
    }

    if (this.selectedFinding()?.resolvedOn || this.selectedFinding()?.resolvedUser || this.selectedFinding()?.comment) {
      subsections.push(
        {
          title: this.findingsTexts.tabs.details.subsections.resolution.title,
          subSectionProps: {
            ...this._resolutionSubSections(),
          }
        }
      );
    }

    return subsections;

  }

  private _violationDetailsSubSections(): DrawerSubSectionProps {
    const data = {
      ...this.selectedFinding(),
      what: this._eventsWhatHappened(),
      srcMacs: this._extractMacs((this.selectedFinding()?.srcAssets.nodes ?? []) as LeanAsset[]),
      dstMacs: this._extractMacs((this.selectedFinding()?.dstAssets.nodes ?? []) as LeanAsset[]),
      policyHits: [
        `${this.selectedFinding()?.activeHits || 0} ${this.findingsTexts.header.similar_findings.active}`,
        `${this.selectedFinding()?.resolvedHits || 0} ${this.findingsTexts.header.similar_findings.resolved}`
      ],
    };

    return {
      subSectionType: SubSectionType.keyValueGrid,
      subSectionContent: {
        data: data,
        gridOptionsOnly: true,
        removeEmptyFields: true,
        hideEmptyTable: true,
        gridOptions: {
          components: {...this.keyValueGridComponents},
          columnDefs: [
            {
              field: 'what',
              headerName: this.findingsTexts.tabs.details.subsections.violation_details.what,
            },
            {
              ...this.policyFindingsGridSettingsService.MITRE_TACTICS,
              hide: false
            },
            {
              ...this.policyFindingsGridSettingsService.MITRE_TECHNIQUE,
              hide: false
            },
            {
              ...this.policyFindingsGridSettingsService.SRC_ASSETS,
              headerName: this.findingsTexts.tabs.details.subsections.violation_details.src_name,
            },
            {
              ...this.policyFindingsGridSettingsService.SRC_IP,
              headerName: this.findingsTexts.tabs.details.subsections.violation_details.src_ip,
            },
            {
              field: 'srcMacs',
              headerName: this.findingsTexts.tabs.details.subsections.violation_details.src_mac,
            },
            {
              ...this.policyFindingsGridSettingsService.DST_ASSETS,
              headerName: this.findingsTexts.tabs.details.subsections.violation_details.dest_name,
            },
            {
              ...this.policyFindingsGridSettingsService.DST_IP,
              headerName: this.findingsTexts.tabs.details.subsections.violation_details.dest_ip,
            },
            {
              field: 'dstMacs',
              headerName: this.findingsTexts.tabs.details.subsections.violation_details.dest_mac,
            },
            {
              ...this.policyFindingsGridSettingsService.PROTOCOL,
              headerName: this.findingsTexts.tabs.details.subsections.violation_details.protocol,
            },
            {
              ...this.policyFindingsGridSettingsService.VIOLATION_TYPE,
              headerName: this.findingsTexts.tabs.details.subsections.violation_details.violation_type,
            },
            {
              ...this.policyFindingsGridSettingsService.VIOLATION_CATEGORY,
              headerName: this.findingsTexts.tabs.details.subsections.violation_details.violation_category,
              hide: false
            },
            {
              field: 'policyHits',
              headerName: this.findingsTexts.tabs.details.subsections.violation_details.policy_hit,
            },
            {
              field: 'firstHitTime',
              headerName: this.findingsTexts.tabs.details.subsections.violation_details.first_hit,
              valueGetter: (params: ValueGetterParams) => params?.data?.firstHitTime && DateTimeService.utcToLocalFormatted(params.data?.firstHitTime, DatesFormatsEnum.general),

            },
            {
              ...this.policyFindingsGridSettingsService.LAST_HIT,
              headerName: this.findingsTexts.tabs.details.subsections.violation_details.last_hit,
            },
          ],
        },
      },
    };
  }

  private _pluginDetailsSubSections(): DrawerSubSectionProps {
    const data = {
      ...this.selectedFinding(),
    };

    return {
      subSectionType: SubSectionType.keyValueGrid,
      subSectionContent: {
        data: data,
        gridOptionsOnly: true,
        removeEmptyFields: true,
        hideEmptyTable: true,
        gridOptions: {
          components: {...this.keyValueGridComponents},
          columnDefs: [
            {
              field: 'pluginName',
              headerName: this.findingsTexts.tabs.details.subsections.plugin.name,
            },
            {
              field: 'pluginId',
              headerName: this.findingsTexts.tabs.details.subsections.plugin.id,
            },
            {
              field: 'severity',
              cellRenderer: 'severityCellRendererComponent',
              headerName: this.findingsTexts.tabs.details.subsections.plugin.severity,
            },
            {
              field: 'pluginSynopsis',
              headerName: this.findingsTexts.tabs.details.subsections.plugin.synopsis,
            },
            {
              field: 'pluginDescription',
              headerName: this.findingsTexts.tabs.details.subsections.plugin.description,
            },
            {
              field: 'pluginSolution',
              headerName: this.findingsTexts.tabs.details.subsections.plugin.solution,
            },

          ],
        },
      },
    };
  }

  private _resolutionSubSections(): DrawerSubSectionProps {
    const data = {
      ...this.selectedFinding(),
    };

    return {
      subSectionType: SubSectionType.keyValueGrid,
      subSectionContent: {
        data: data,
        gridOptionsOnly: true,
        removeEmptyFields: true,
        hideEmptyTable: true,
        gridOptions: {
          components: {...this.keyValueGridComponents},
          columnDefs: [
            {
              field: 'resolvedOn',
              valueGetter: (params) => params?.data?.resolvedOn && DateTimeService.utcToLocalFormatted(params.data?.resolvedOn, DatesFormatsEnum.general),
              headerName: this.findingsTexts.tabs.details.subsections.resolution.resolved_at,
            },
            {
              field: 'resolvedUser',
              headerName: this.findingsTexts.tabs.details.subsections.resolution.resolved_by,
            },
            {
              field: 'comment',
              headerName: this.findingsTexts.tabs.details.subsections.resolution.comment,
            },

          ],
        },
      },
    };
  }

  private _getEventType(type: PolicyEventType): EventType | undefined {
    const policyEventType: PolicyEventTypeEnums = PoliciesDataHelperService.convertInactiveAsset(type);
    return this.eventsTypesService.eventsTypes.get(policyEventType);
  }

  private _whyImportantMsg(): string | undefined {
    const eventType = this.selectedFinding()?.eventType?.type;
    if (eventType) {
      const displayName = this._getEventType(eventType);
      if (displayName) {
        return this.languageService.getResource(displayName.name + '.why_important', NsEnum.eventsDescriptionTexts);
      }
    }
    return undefined;
  }

  private _eventsWhatHappened(): string | undefined {
    const eventType = this.selectedFinding()?.eventType?.type;
    if (eventType) {
      const displayName = this._getEventType(eventType);
      if (displayName) {
        return this.languageService.getResource(displayName.name + '.description', NsEnum.eventsDescriptionTexts);
      }
    }
    return undefined;
  }

  private _mitigationMsg(): string | undefined {
    const eventType = this.selectedFinding()?.eventType?.type;
    if (eventType) {
      const displayName = this._getEventType(eventType);
      if (displayName) {
        return this.languageService.getResource(displayName.name + '.suggested_mitigation', NsEnum.eventsDescriptionTexts);
      }
    }
    return undefined;
  }
  //endregion

  //region Source Tab subsections
  private _getSourceSubSections(): DrawerSubSection[] {
    const subsections: DrawerSubSection[] = [
    ];

    const columnDef = this._sourceColumnDef();

    const isSingleSource = this.selectedFinding()?.srcAssets?.nodes?.length === 1;

    this.selectedFinding()?.srcAssets?.nodes.forEach((srcAsset, i) => {
      const actionButtons: ActionButtonDefinition[] = isSingleSource ? [] :
        [this._assetLinkActionButton(srcAsset.id, this.findingsTexts.tabs.source.subsections.link)];
        subsections.push({
          title: !isSingleSource ? this.languageService.getTranslationTyped('findings.policy_violations.side_drawer.tabs.source.subsections.title', {index: i + 1}) : undefined,
          actionButtons: actionButtons,
          subSectionProps: {
            subSectionType: SubSectionType.keyValueGrid,
            subSectionContent: {
              data: srcAsset,
              gridOptionsOnly: true,
              removeEmptyFields: true,
              hideEmptyTable: true,
              gridOptions: {
                components: {...this.keyValueGridComponents},
                columnDefs: columnDef,
              },
            },
          }
        });
      });

    return subsections;
  }

  private _sourceColumnDef(): ColDef[] {
    const srcTabTexts = this.findingsTexts.tabs.source.subsections;
    return [
      {
        headerName: srcTabTexts.name,
        field: 'name',
        minWidth: 200,
      },
      {
        headerName: srcTabTexts.type,
        field: 'type',
        cellRenderer: 'assetTypeRenderer',
      },
      {
        headerName: srcTabTexts.risk_score,
        field: 'risk',
        cellRenderer: 'riskScoreRenderer'
      },
      {
        headerName: srcTabTexts.ips,
        field: 'ips.nodes',
        valueFormatter: params => Array.isArray(params.value) ? params.value.join(DelimitersEnum.pipe) : params.value,
      },
      {
        headerName: srcTabTexts.macs,
        field: 'macs.nodes',
        valueFormatter: params => Array.isArray(params.value) ? params.value.join(DelimitersEnum.pipe) : params.value,
      },
      {
        headerName: srcTabTexts.backplane,
        field: 'backplane.name'
      },
      {
        headerName: srcTabTexts.slot,
        field: 'slot'
      },
      {
        headerName: srcTabTexts.asset_criticality,
        field: 'criticality',
        cellRenderer: 'criticalityRenderer'
      },
      {
        headerName: srcTabTexts.vendor,
        field: 'vendor',
        cellRenderer: 'assetVendorRenderer',
      },
      {
        headerName: srcTabTexts.model,
        field: 'model',
      },
      {
        headerName: srcTabTexts.purdue_level,
        field: 'purdueLevel',
        valueFormatter: params => {
          return this.languageService.formatEnum('PurdueLevel', params.value);
        },
      },
      {
        headerName: srcTabTexts.location,
        field: 'location',
      },
      {
        headerName: srcTabTexts.description,
        field: 'description',
      }
    ];
  }
  //endregion

  //region Destination Tab subsections
  private _getDestinationSubSections(): DrawerSubSection[] {
    const subsections: DrawerSubSection[] = [
    ];

    const columnDef = this._destColumnDef();

    const isSingleDest = this.selectedFinding()?.dstAssets?.nodes?.length === 1;

    this.selectedFinding()?.dstAssets?.nodes.forEach((destAsset, i) => {
      const actionButtons: ActionButtonDefinition[] = isSingleDest ? [] :
        [this._assetLinkActionButton(destAsset.id, this.findingsTexts.tabs.destination.subsections.link)];
      subsections.push({
        title: !isSingleDest ? this.languageService.getTranslationTyped('findings.policy_violations.side_drawer.tabs.destination.subsections.title', {index: i + 1}) : undefined,
        actionButtons: actionButtons,
        subSectionProps: {
          subSectionType: SubSectionType.keyValueGrid,
          subSectionContent: {
            data: destAsset,
            gridOptionsOnly: true,
            removeEmptyFields: true,
            hideEmptyTable: true,
            gridOptions: {
              components: {...this.keyValueGridComponents},
              columnDefs: columnDef,
            },
          },
        }
      });
    });

    return subsections;
  }

  private _destColumnDef(): ColDef[] {
    const dstTabTexts = this.findingsTexts.tabs.destination.subsections;
    return [
      {
        headerName: dstTabTexts.name,
        field: 'name',
        minWidth: 200,
      },
      {
        headerName: dstTabTexts.type,
        field: 'type',
        cellRenderer: 'assetTypeRenderer',
      },
      {
        headerName: dstTabTexts.risk_score,
        field: 'risk',
        cellRenderer: 'riskScoreRenderer'
      },
      {
        headerName: dstTabTexts.ips,
        field: 'ips.nodes',
        valueFormatter: params => Array.isArray(params.value) ? params.value.join(DelimitersEnum.pipe) : params.value,
      },
      {
        headerName: dstTabTexts.macs,
        field: 'macs.nodes',
        valueFormatter: params => Array.isArray(params.value) ? params.value.join(DelimitersEnum.pipe) : params.value,
      },
      {
        headerName: dstTabTexts.backplane,
        field: 'backplane.name'
      },
      {
        headerName: dstTabTexts.slot,
        field: 'slot'
      },
      {
        headerName: dstTabTexts.asset_criticality,
        field: 'criticality',
        cellRenderer: 'criticalityRenderer'
      },
      {
        headerName: dstTabTexts.vendor,
        field: 'vendor',
        cellRenderer: 'assetVendorRenderer',
      },
      {
        headerName: dstTabTexts.model,
        field: 'model',
      },
      {
        headerName: dstTabTexts.purdue_level,
        field: 'purdueLevel',
        valueFormatter: params => purdueLevel.get(params.value) ?? params.value,
      },
      {
        headerName: dstTabTexts.location,
        field: 'location',
      },
      {
        headerName: dstTabTexts.description,
        field: 'description',
      }
    ];

  }

  //endregion

  //region Policy Tab subsections
  private _getPolicySections(): DrawerSubSection[] {
    return [
      {
        subSectionProps: {
          subSectionType: SubSectionType.keyValueGrid,
          subSectionContent: {
            data: this.policiesDataHelperService.convertToPolicyGridItem(this.selectedFinding()?.policy),
            gridOptionsOnly: true,
            removeEmptyFields: true,
            hideEmptyTable: true,
            gridOptions: {
              components: {...this.keyValueGridComponents},
              columnDefs: this._policyColumnDef(),
            },
          },
        }
      }
    ];

  }

  private _policyColumnDef(): ColDef[] {
    const policyTabTexts = this.languageService.texts.policies;
    return [
      {
        headerName: policyTabTexts.grid_columns.policy_name,
        field: PolicyBackendNameEnum.Title,
      },
      {
        headerName: policyTabTexts.policy_actions.additionalActions.snapshot,
        field: PolicyBackendNameEnum.Snapshot,
      },
      {
        headerName: policyTabTexts.policy_definition.rule_group,
        colId: PolicyBackendNameEnum.RuleGroup,
        field: 'ruleGroup.group.name',
      },
      {
        headerName: policyTabTexts.policy_definition.asset_not_seen,
        field: PolicyBackendNameEnum.NotSeenFor
      },
      {
        headerName: policyTabTexts.policy_definition.protocol,
        field: PolicyBackendNameEnum.ProtocolGroup,
        cellRenderer: 'routerCellRenderer',
        cellRendererParams: (params: IndegyICellRendererParams<PolicyGridItem>): any => {
          return this.policiesGridSettingsService.getGroupCellRenderer(params, RoutesStatesNamesEnum.singleProtocolGroupMembersTab, GroupTypesEnum.ProtocolGroup);
        },
      },
      {
        headerName: policyTabTexts.policy_definition.disable_after_hit,
        field: PolicyBackendNameEnum.DisableAfterHit
      },
      {
        headerName: policyTabTexts.grid_columns.severity,
        field: PolicyBackendNameEnum.Level,
        cellRenderer: 'severityCellRendererComponent',
      },
      ...this.policiesGridSettingsService.POLICIES_TAB_COLUMN_DEFS
    ];
  }

  //endregion

  private _assetExternalURL(assetId: string): string {
    return `${RoutesStatesNamesEnum.singleAssetPage}/${RoutesStatesNamesEnum.assetDetails}?id=${assetId}`;
  }

  private _assetLinkActionButton(assetId: string, title: string): ActionButtonDefinition {
    return {
      variant: ActionButtonVariant.link,
      title: title,
      path: this._assetExternalURL(assetId),
      isExternal: true
    }  ;
  }

  private _extractMacs( assets: LeanAsset[]): string[] {
    return assets?.map(asset  => Utils.stripConnection(asset?.macs)).flat() ?? [];
  }
}
